import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link";
export type ButtonSize = "sm" | "md" | "lg" | "icon" | "icon-lg";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-300 dark:disabled:bg-brand-900",
  secondary:
    "bg-card border border-[var(--line-strong)] text-content hover:bg-[var(--surface-hover)] shadow-[var(--shadow-card)]",
  ghost: "text-content-secondary hover:bg-[var(--surface-hover)] hover:text-content",
  danger: "bg-danger text-white hover:brightness-110 active:brightness-95",
  link: "text-brand-700 dark:text-brand-400 hover:underline underline-offset-4 p-0 h-auto",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-2.5 text-[0.8125rem] gap-1.5",
  md: "h-9 px-3.5 text-sm gap-2",
  lg: "h-10 px-5 text-sm gap-2",
  // Square. 36px is a deliberate density tradeoff — still comfortably above
  // WCAG 2.2's 24px minimum target size.
  icon: "h-9 w-9 p-0 shrink-0",
  // Phone chrome, where a thumb needs 40px and WCAG 2.2's 44px is the target.
  "icon-lg": "h-10 w-10 p-0 shrink-0",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks interaction. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    type = "button",
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // Defaults to "button", not "submit". An unspecified type inside a form is
      // a submit button, so a "Cancel" or "Add row" button silently submits the
      // form — a bug that is invisible until someone presses Enter.
      type={type}
      // Disabled while loading so a double-click cannot fire the mutation twice.
      disabled={disabled === true || loading}
      // Communicates the pending state to assistive technology, which cannot see
      // the spinner.
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-md font-medium",
        "transition-colors duration-150",
        "disabled:cursor-not-allowed disabled:opacity-60",
        // Whitespace must not collapse: a wrapped label in a table toolbar
        // reflows the whole row.
        "whitespace-nowrap",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 aria-hidden className="size-4 animate-spin" />
      ) : (
        leadingIcon
      )}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});
